<template>
	<model-viewer
		:src="$store.state.activeModel"
		environment-image="neutral"
		camera-orbit="131.5deg 65.77deg 75%"
		min-camera-orbit="auto 0deg auto"
		max-camera-orbit="auto 94deg auto"
		animation-crossfade-duration="500"
		animation-control-mode="manual"
		interaction-prompt-style="basic"
		:interaction-prompt="modelPrompt"
		camera-controls
		:class="{ 'hotspots-visible': activePrimaryNavigation === 'keyFeatures' && activeSecondaryNavigation }"
		@progress="updateProgress"
		@load="onModelLoad"
		@camera-change="onCameraChange"
		v-html="$store.state.productConfig.hotspots"
	/>

	<div
		v-if="$store.state.debug"
		class="debug-info"
	>
		Orbit: {{ cameraOrbit }}<br>
		Target: {{ cameraTarget }}
	</div>
</template>

<script>
import { mapState } from 'vuex'
import 'model-viewer'

export default {
	name: 'ModelView',
	props: {
		playing: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			modelPrompt: 'auto',
			/** @type {?HTMLElement} */
			modelViewer: null,
			/** @type {[Node]} */
			hotspots: [],
			/** @type {Object.<string, THREE.AnimationAction>} */
			modelActions: {},
			/** @type {number} */
			animationsInProgress: 0,

			/** @type {?string} */
			lastPlayedName: null,
			/** @type {?THREE.AnimationAction} */
			lastPlayedAction: null,
			/** @type {?string} */
			activePresetAction: null,
			/** @type {?string} */
			activePreset: null,

			initialCameraOrbit: '',
			cameraOrbit: '',
			cameraTarget: ''
		}
	},
	computed: mapState({
		activePrimaryNavigation: state => state.navigation.activePrimary,
		activeSecondaryNavigation: state => state.navigation.activeSecondary,
		activeSubExplanation: state => state.explanationPaneSub,
		explanationPaneOpen: state => state.explanationPaneOpen,
		animationsList: state => state.productConfig.animations,
		modelLoadingProgress: state => state.modelLoadingProgress,
		productConfig: state => state.productConfig,
    transferModeOn: state => state.navigation.transferModeOn,
    siderailsCheck: state => state.navigation.siderailsCheck
	}),
	mounted() {
		this.$bus.on('requestModelAnimation', async ({ name }) => {
			const isPreset = this.animationsList[name]?.preset

			if (isPreset) {
				if (this.activePreset === name) {
					// reset when same preset position is clicked again
					this.resetModel()
				} else {
					this.positionHandlers().preset(name)
				}

			} else if (this.positionHandlers()[name]) {
				this.positionHandlers()[name]()
			}
		})

		this.$bus.on('requestModelAnimationsReset', () => {
			this.resetModel()
		})

		this.$bus.on('requestModelPartsVisibility', nodes => {
			nodes.forEach(node => {this.setModelNodeVisibility(node.name, node.visible)})
		})

		this.$bus.on('requestCameraPosition', cameraPosition => {
			this.setCameraPosition(cameraPosition)
		})

		this.$bus.on('requestColorChange', color => {
			localStorage.setItem('gracieColor', color)
			this.setModelColor(this.productConfig.colors[color])
		})

		window.modelVM = this // TODO: for debugging only, remove when going into production
	},
	methods: {
		onModelLoad(e) {
			const viewer = e.target
			const actions = {}

			// add all available animations to mixer and set reference for easy access
			for (const [name, animation] of Object.entries(this.animationsList)) {
				const animationTrack = viewer.scene.animationsByName.get(animation.track)

				if (animationTrack) {
					const action = viewer.animationMixer.clipAction(animationTrack)

					action.setLoop(viewer.availableLoopModes.once)
					action.clampWhenFinished = true

					actions[name] = action
				}
			}

			// set up hotspots (positioning is done by modelviewer)
			viewer.querySelectorAll('button').forEach(hotspot => {
				hotspot.setAttribute('disabled', '')
				hotspot.addEventListener('click', () => this.onHotspotClick(hotspot))
				this.hotspots.push(hotspot)
			})

			this.modelActions = actions
			this.modelViewer = viewer
			this.initialCameraOrbit = viewer.cameraOrbit

			window.modelViewer = viewer
      window.modelActions = actions

			// hide Gracie extras by default
			this.setModelNodeVisibility('stolek_loc', false)
			this.setModelNodeVisibility('gracieZidle_grp', false)
		},

		isAnimationActive(name) {
			return this.modelActions[name]?.time > 0
		},

		async animateSubsequentActions(actionNames = [], speed = 1) {
			for (const [i, actionName] of actionNames.entries()) {
				this.modelActions[actionName].setEffectiveWeight(1)
				await this.playAnimation(actionName, { speed, fadeIn: i === 0 })

				if (i < actionNames.length - 1) { // reset except for the last action
					this.modelActions[actionName].setEffectiveWeight(0)
					this.modelActions[actionName].time = 0
				}
			}
			return Promise.resolve()
		},

		positionHandlers() {
			return {
				'lowest_position': () => {
					if (
						this.isAnimationActive('low600') ||
						this.activePreset === 'entering_position'
					) return

					if (this.isAnimationActive('backrest50')) this.positionHandlers()['position_in_laying_rev']()

					const loweringAnimations = new Set(['low1150', 'low900', 'low750', 'low600'])
					if ( ['examination_position', 'ultrasonic'].includes(this.activePreset) )
						loweringAnimations.delete('low1150')

					this.fadeAnimations(['high1150'], 1)
					this.animateSubsequentActions([...loweringAnimations], 2)
				},
				'highest_position': () => {
						if (
							this.isAnimationActive('high1150') ||
							!this.lastPlayedName
						)  return

						if ( this.activePreset === 'examination_position' ) {
							this.playAnimation('high1150')
						} else {
							this.fadeAnimations(['low600'], 1)
							this.animateSubsequentActions(['high600', 'high750', 'high900', 'high1150'], 2)
						}
				},
				'position_in_laying': async () => {
					const sequence = ['backrest5', 'backrest35', 'backrest50']
					const activeStep = sequence.find( it => this.isAnimationActive(it) )

					switch (activeStep) {
						case 'backrest5':
							await this.playAnimation('backrest5', { reverse: true })
							this.modelActions['backrest5'].setEffectiveWeight(0)
							break;
						case 'backrest35':
							if (this.isAnimationActive('low600')) break
							this.fadeAnimations(['backrest35', 'feet_down'], 1)
							this.playAnimation('backrest50')
							break
						case 'backrest50':
							break
						default:
							this.fadeAnimations(['feet_down'], 1)
							this.playAnimation('backrest35')
							break
					}
				},
				'position_in_laying_rev': async () => {
					const sequence = ['backrest5', 'backrest35', 'backrest50']
					const activeStep = sequence.find( it => this.isAnimationActive(it) )

					switch (activeStep) {
						case 'backrest50':
							await this.playAnimation('backrest50', { reverse: true })
							this.modelActions['backrest35'].time = this.modelActions['backrest35'].getClip().duration
							this.modelActions['backrest35'].setEffectiveWeight(1)
							this.modelActions['backrest35'].enabled = true
							this.modelActions['backrest35'].paused = false
							this.modelActions['backrest50'].setEffectiveWeight(0)
							break
						case 'backrest35':
							await this.playAnimation('backrest35', { reverse: true })
							this.modelActions['backrest35'].setEffectiveWeight(0)
							break
						case 'backrest5':
							break
						default:
							this.playAnimation('backrest5')
							break
					}
				},
				'feet_up': () => {
					if (
						this.isAnimationActive('feet_up') ||
						this.activePreset === 'entering_position'
					) return

					if (this.isAnimationActive('feet_down')) {
						this.fadeAnimations(['feet_down'], 1.5)
						//this.playAnimation('feet_down', { reverse: true })
					} else {
						this.playAnimation('feet_up')
					}
				},
				'feet_down': () => {
					if (
						this.isAnimationActive('feet_down') ||
						( this.isAnimationActive('backrest35') && !this.isAnimationActive('feet_up') ) ||
						( this.isAnimationActive('backrest50') && !this.isAnimationActive('feet_up') ) ||
						this.activePreset === 'entering_position'
					) return

					if (this.isAnimationActive('feet_up')) {
						this.fadeAnimations(['feet_up'], 1.5)
						//this.playAnimation('feet_up', { reverse: true })
					} else {
						this.playAnimation('feet_down')
					}
				},
				'legs20degrees': () => {
					this.playAnimation('legs20degrees', { reverse: this.isAnimationActive('legs20degrees') })
				},
				'colposcope': () => {
					this.playAnimation('colposcope', { reverse: this.isAnimationActive('colposcope') })
				},

				// this one handles all preset positions
				'preset': async (name) => {
					// using special transition animation if possible
					const actionName = this.animationsList[this.activePreset]?.transitions?.[name] || name
					const toFade = new Set(['backrest5', 'backrest35', 'backrest50', 'feet_up', 'feet_down', 'low600', 'high1150'])
					let fadeDuration = 2
					let start = 0

					switch (name) {
						case 'entering_position':
							if (!this.activePresetAction) this.modelActions['low600'].setEffectiveWeight(100)
							toFade.delete('low600')
							break;
						case 'ultrasonic':
							if (this.lastPlayedName === 'low600') start = 6
							break
						case 'trendelenburg':
							toFade.clear()
					}

					this.fadeAnimations(toFade, fadeDuration)

					const speed = this.activePresetAction ? 1 : 2
					this.playAnimation(actionName, { start, blendFrom: this.activePresetAction, speed })
					this.activePreset = name
					this.activePresetAction = actionName

				}
			}
		},

		async playAnimation(name, { reverse = false, blendFrom = '', speed = 1, fadeIn = true, start = 0 } = {}) {
			console.log(`${reverse ? 'Reversing' : 'Playing'} animation: ${name}${blendFrom ? ', blending from ' + blendFrom : ''}`)

			const action = this.modelActions[name]
			const timeScaleSign = reverse ? -1 : 1
			const duration = action.getClip().duration / speed

			// start the rendering loop
			this.modelViewer.play()

			if (!reverse) {
				action.reset()
				action.time = start
			}

			if (blendFrom) {
				action.crossFadeFrom(this.modelActions[blendFrom], duration)
			} else {
				action.setEffectiveTimeScale(speed * timeScaleSign)
				action.setEffectiveWeight(1)
				action.enabled = true
				action.paused = false
			}

			if (fadeIn && !reverse) action.fadeIn(1)
			action.play()

			this.lastPlayedName = name
			this.lastPlayedAction = action
			this.animationsInProgress++

			if (blendFrom) {
				await new Promise(res => setTimeout(res, duration * 1000))
			} else {
				await this.onAnimationFinished(name)
			}

			// save resources by pausing the rendering loop when no animations are playing
			if (this.animationsInProgress === 1) this.modelViewer.pause()
			this.animationsInProgress--
			console.log(`${reverse ? 'Reversing' : 'Animation'} ${name} finished`)

			return
		},

		onAnimationFinished(name) {
			const action = this.modelActions[name]
			return new Promise(resolve => {
				// set up listener when animation finishes
				this.modelViewer.animationMixer.addEventListener('finished', action.finishedCallback = e => {
					const finishedTrackName = e.action.getClip().name

					if (finishedTrackName === this.animationsList[name].track) {
						this.modelViewer.animationMixer.removeEventListener('finished', action.finishedCallback)
						resolve()
					}
				})
			})
		},

		async fadeAnimations(names, time = 1) {
			this.modelViewer.play()

			const promises = []
			names.forEach(name => {
				const action = this.modelActions[name]

				promises.push(new Promise(resolve => {
					if (action.time === 0) {
						resolve()
						return
					}
					action.fadeOut(time)
					this.animationsInProgress++

					setTimeout(() => {
						action.time = 0
						this.animationsInProgress--
						resolve()
					}, time * 1000)
				}))
			})

			return Promise.all(promises).then(() => {
				// save resources by pausing the rendering loop when no animations are playing
				if (this.animationsInProgress === 0) this.modelViewer.pause()
			})
		},

		async resetModel() {
			console.log('Resetting all animations on model')

			this.activePreset = null
			this.activePresetAction = null

			await this.fadeAnimations(Object.keys(this.modelActions), 2)

			this.lastPlayedName = null
			this.lastPlayedAction = null
		},

		setModelNodeVisibility(name, visible) {
			const primitive = this.modelViewer.model.primitives.find(item => item.name === name)
			const object3d = this.modelViewer.scene.getChildByName(name)

			this.modelViewer.play()

			if (primitive) primitive.setMeshVisibility(visible)
			if (object3d) object3d.visible = visible

			setTimeout(() => {
				if (this.animationsInProgress === 0) this.modelViewer.pause()
			}, 100)
		},

		setModelColor(color) {
			console.log(this.modelViewer.model.materials)
			this.modelViewer.model.materials.find(item => item.name === 'gracieGltfMat_GLSLShader8oranzovaKuze').pbrMetallicRoughness.setBaseColorFactor(color)
			this.modelViewer.model.materials.find(item => item.name === 'GLSLShader4orange_mat').pbrMetallicRoughness.setBaseColorFactor(color)
		},

		updateProgress(e) {
			this.$store.commit('setModelLoadingProgress', e.detail.totalProgress)
		},

		hideInteractionPrompt() {
			this.modelPrompt = 'none'
		},

		setCameraPosition(position) {
			this.modelViewer.cameraOrbit = position.orbit === 'initial' ? this.initialCameraOrbit : position.orbit
			this.modelViewer.cameraTarget = position.target === 'initial' ? '0 0 0' : position.target
		},

		resetCameraPosition() {
			this.setCameraPosition({
				orbit: this.initialCameraOrbit,
				target: 'auto auto auto'
			})
		},

		onHotspotClick(hotspot) {
			const explanations = hotspot.dataset.explanations?.split('.')
			if (explanations) {
				const explanation = explanations[0]
				const subExplanation = explanations[1] || null
        const explanationItem = this.productConfig.explanations[explanation]
        const subExplanationItem = this.productConfig.explanations[subExplanation]
				this.$store.commit('navigation/setActiveSecondary', 'key_features.' + explanation)
				this.$store.commit('setExplanationPaneContent', explanationItem)
				this.$store.commit('setExplanationPaneSub', subExplanation)
				this.$store.commit('setExplanationPaneOpen', true)

        if (subExplanationItem?.camera) {
          this.setCameraPosition(subExplanationItem.camera)
        }
			}
		},

		onCameraChange() {
			this.cameraOrbit = this.modelViewer?.getCameraOrbit() || ''
			this.cameraTarget = this.modelViewer?.getCameraTarget() || ''
		}
	},
  watch: {
		activeSecondaryNavigation(val) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// disable hotspots not related to active feature
				this.hotspots.forEach(hotspot => {
					const navItemName = val.split('.')[1]
					const hotspotExplanations = hotspot.dataset.explanations?.split('.')
					hotspot.classList.remove('active')
					if (hotspotExplanations[0] === navItemName) {
						if (hotspotExplanations.length === 1) {
							hotspot.classList.add('active')
						}
						hotspot.removeAttribute('disabled')
					} else {
						hotspot.setAttribute('disabled', '')
					}
				})
			}
		},
		activeSubExplanation(name) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// display hotspot as active when related feature + subExplanation is selected
				this.hotspots.forEach(hotspot => {
					const hotspotExplanation = hotspot.dataset.explanations?.split('.')[1]
					if (hotspotExplanation === name) {
						hotspot.classList.add('active')
					} else {
						hotspot.classList.remove('active')
					}
				})
			}
		},
		explanationPaneOpen(open) {
			if (!open) {
				this.$nextTick(() => {
					this.resetCameraPosition()
				})
			}
		}
	},
}
</script>

<style lang="scss" scoped>
model-viewer {
	width: 100%;
	height: 100%;
	--poster-color: transparent;
	--min-hotspot-opacity: 0;

	&::part(default-progress-bar) {
		top: 30px;
		background-color: $clrRed;
	}
}

::v-deep(.Hotspot) {
	display: none;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: transparentize($color: $clrWhite, $amount: 0.2);
	border-radius: 100px;
	box-shadow: 0 0 0 4px transparentize($color: $clrWhite, $amount: 0.5);
	transition: all 300ms ease 200ms;
	&.active,
	&:hover,
	&:disabled,
	&:not([data-visible]) {
		background-color: transparent;
		box-shadow: 0 0 0 0 transparent;
		transition: all 300ms ease;
	}
	&:disabled {
		pointer-events: none;
	}
	.hotspots-visible & {
		display: block;
	}

	&::before {
		content: "";
		position: absolute;
		width: 17px;
		height: 17px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		border-radius: 100px;
		border: 1px solid $clrWhite;
		background-color: $clrRed;
		background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M128 29.799v196.403M226.202 128H29.799' stroke-linecap='round' stroke-miterlimit='10' stroke-width='51.7px' fill='none' stroke='%23FFFFFF'/%3E%3C/svg%3E%0A");
		background-size: 10px;
		background-repeat: no-repeat;
		background-position: center;
		transition: all 300ms ease 200ms;
	}
	&.active::before,
	&:hover::before,
	&:disabled::before,
	&:not([data-visible])::before {
		transform: translate(-50%, -50%) scale(0.6);
		opacity: 0;
		transition: all 300ms ease;
	}

	.HotspotAnnotation {
		position: absolute;
		left: 50%;
		bottom: 24px;
		padding: 0.2em 0.75em;
		transform: translate(-50%, 30%);
		opacity: 0;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
		border-radius: 100px;
		background-color: $clrWhite;
		transition: all 300ms ease;
		pointer-events: none;
		z-index: 100;

		&::before {
			content: "";
			position: absolute;
			width: 0;
			height: 0;
			bottom: -6px;
			left: calc(50% - 6px);
			border-style: solid;
			border-width: 6px 6px 0 6px;
			border-color: #ffffff transparent transparent transparent;
		}
	}
	&:hover, &.active {
		.HotspotAnnotation {
			opacity: 1;
			transform: translate(-50%, 0);
			pointer-events: all;
			color: $clrRed;
		}
	}
}

.debug-info {
	position: absolute;
	top: 50px;
	left: 300px;
	font-size: 10px;
}
</style>
